/**
 * NLToSQLPopover — Natural language to SQL expression popover.
 *
 * Triggered by the wand icon in ColumnMappingGrid rows.
 * Provides a NL description input (max 500 chars — matches backend validation).
 * Calls POST /jobs/{job_id}/nl-to-sql and shows the returned expression.
 * "Use this" button calls onExpressionSelected() to populate src_expression.
 *
 * Closes on Escape key or click-outside.
 * Loading state: spinner while awaiting LLM.
 * Error state: red inline message when result.error is set.
 *
 * Safety: sends only target_col (column name) and nl_description (user text).
 * No sample values, column values, or SENSITIVE_KEYS are ever sent.
 */

import { useCallback, useEffect, useRef } from "react";
import { Loader2, Wand2, X, Check } from "lucide-react";
import { useNLtoSQL } from "../../../features/pipeline-engineer/useAgenticApi";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_DESCRIPTION_CHARS = 500;

const INPUT_CLASS =
  "w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/30";

const LABEL_CLASS = "block text-xs font-medium text-white/60 mb-1";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface NLToSQLPopoverProps {
  jobId: string;
  projectId: string;
  /** Target column name for context — shown as a label. */
  targetCol: string;
  /** Called with the SQL expression when user clicks "Use this". */
  onExpressionSelected: (expression: string) => void;
  /** Called when popover should close. */
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function NLToSQLPopover({
  jobId,
  projectId,
  targetCol,
  onExpressionSelected,
  onClose,
}: NLToSQLPopoverProps) {
  const nltoSql = useNLtoSQL(jobId, projectId);
  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const descriptionRef = useRef<string>("");

  // Focus textarea on mount
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  // Close on Escape
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
      }
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  // Close on click-outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        containerRef.current !== null &&
        !containerRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    }
    // Use mousedown so the popover closes before any click handlers on the outside element fire
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    const desc = descriptionRef.current.trim();
    if (!desc) return;
    await nltoSql.translate(targetCol, desc);
  }, [targetCol, nltoSql]);

  const handleUseThis = useCallback(() => {
    if (nltoSql.result?.expression != null) {
      onExpressionSelected(nltoSql.result.expression);
      onClose();
    }
  }, [nltoSql.result, onExpressionSelected, onClose]);

  const hasExpression =
    nltoSql.result !== null && nltoSql.result.expression !== null;
  const hasError = nltoSql.result !== null && nltoSql.result.error !== null;

  return (
    <div
      ref={containerRef}
      className="absolute z-50 left-0 top-full mt-1 w-80 bg-[#1a1f2e] border border-white/15 rounded-xl shadow-2xl p-4 space-y-3"
      role="dialog"
      aria-label={`NL to SQL for ${targetCol}`}
      aria-modal="false"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Wand2 size={13} className="text-brand-400" aria-hidden="true" />
          <span className="text-xs font-semibold text-white/80">NL → SQL</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close NL to SQL popover"
          className="p-1 text-white/30 hover:text-white/60 transition-colors rounded"
        >
          <X size={12} aria-hidden="true" />
        </button>
      </div>

      {/* Target column context */}
      <div className="bg-white/5 rounded-lg px-3 py-1.5 flex items-center gap-2">
        <span className="text-xs text-white/40">Target column:</span>
        <span className="text-xs font-mono text-white/80">{targetCol}</span>
      </div>

      {/* NL description input */}
      <div>
        <label htmlFor={`nl-desc-${targetCol}`} className={LABEL_CLASS}>
          Describe the transformation{" "}
          <span className="text-white/30">
            (max {MAX_DESCRIPTION_CHARS} chars)
          </span>
        </label>
        <textarea
          id={`nl-desc-${targetCol}`}
          ref={textareaRef}
          rows={3}
          maxLength={MAX_DESCRIPTION_CHARS}
          className={`${INPUT_CLASS} text-xs resize-none`}
          placeholder={`e.g. Concatenate first_name and last_name with a space for target column ${targetCol}`}
          onChange={(e) => {
            descriptionRef.current = e.target.value;
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              void handleSubmit();
            }
          }}
        />
        <p className="text-xs text-white/20 mt-0.5">Ctrl+Enter to submit</p>
      </div>

      {/* Submit button */}
      <button
        type="button"
        onClick={() => void handleSubmit()}
        disabled={nltoSql.loading}
        className="flex items-center gap-1.5 w-full px-3 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed justify-center"
      >
        {nltoSql.loading ? (
          <Loader2 size={12} className="animate-spin" aria-hidden="true" />
        ) : (
          <Wand2 size={12} aria-hidden="true" />
        )}
        {nltoSql.loading ? "Translating…" : "Generate SQL"}
      </button>

      {/* Error state */}
      {hasError && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
          <p className="text-red-400 text-xs" role="alert">
            {nltoSql.result?.error}
          </p>
          <button
            type="button"
            onClick={() => nltoSql.reset()}
            className="text-xs text-red-300 underline hover:no-underline mt-1"
          >
            Try again
          </button>
        </div>
      )}

      {/* Expression result */}
      {hasExpression && (
        <div className="space-y-2">
          <p className="text-xs text-white/50">Generated expression:</p>
          <pre className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-emerald-300 whitespace-pre-wrap break-all max-h-24 overflow-y-auto">
            {nltoSql.result?.expression}
          </pre>
          <button
            type="button"
            onClick={handleUseThis}
            className="flex items-center gap-1.5 w-full px-3 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 rounded-lg text-xs font-medium transition-colors justify-center"
          >
            <Check size={12} aria-hidden="true" />
            Use this
          </button>
        </div>
      )}
    </div>
  );
}
